// Shared social-links row for cards. Reads the URLs an admin entered on the item
// (handles both `instagram` and `instagramUrl` style keys) and renders ONLY the
// icons that actually have a URL — so a card with just an Instagram shows just
// that one, and a card with none shows nothing (instead of dead links to the
// platform homepages). Used by Restaurants / Desserts / Grocery featured cards.

import type { FieldDef } from "../admin/AdminFieldModal";

type AnyItem = Record<string, any>;

// Reusable edit-form fields for social/website links. Append to any page's
// FieldDef list so admins can set these per card; SocialLinks renders whichever
// are filled. Keys match what SocialLinks reads.
export const SOCIAL_FIELDS: FieldDef[] = [
  { key: "website", label: "Website URL", type: "url", placeholder: "https://…" },
  { key: "instagram", label: "Instagram URL", type: "url", placeholder: "https://instagram.com/…" },
  { key: "facebook", label: "Facebook URL", type: "url", placeholder: "https://facebook.com/…" },
  { key: "youtube", label: "YouTube URL", type: "url", placeholder: "https://youtube.com/…" },
  { key: "tiktok", label: "TikTok URL", type: "url", placeholder: "https://tiktok.com/…" },
];

// Admins often paste a URL without the "https://" prefix (e.g. "instagram.com/foo"
// or just "@handle") — the old strict regex silently dropped anything that wasn't
// already a full https:// URL, so the icon just never appeared with no error. This
// normalizes common shapes into a real URL before the strict check, per platform.
export function normalizeSocialUrl(v: any, platform?: string): string {
  if (typeof v !== "string") return "";
  let t = v.trim();
  if (!t) return "";
  if (/^https?:\/\//i.test(t)) return t;
  // "@handle" — only meaningful for handle-based platforms.
  if (t.startsWith("@")) {
    const handle = t.slice(1);
    if (platform === "instagram") return `https://instagram.com/${handle}`;
    if (platform === "tiktok") return `https://tiktok.com/@${handle}`;
    if (platform === "facebook") return `https://facebook.com/${handle}`;
    return "";
  }
  // Bare domain/path like "instagram.com/foo" or "www.site.com" — add the scheme.
  if (/^[\w.-]+\.[a-z]{2,}(\/.*)?$/i.test(t)) return `https://${t}`;
  return "";
}

const first = (...vals: any[]): string => {
  for (const v of vals) {
    const n = normalizeSocialUrl(v);
    if (n) return n;
  }
  return "";
};

const firstFor = (platform: string, ...vals: any[]): string => {
  for (const v of vals) {
    const n = normalizeSocialUrl(v, platform);
    if (n) return n;
  }
  return "";
};

// Counts how many social/website links an item actually has filled in.
// Lets callers decide layout (e.g. share a row when there's just one icon,
// give links their own line when there's more than one).
export function countSocialLinks(item: AnyItem): number {
  return [
    firstFor("instagram", item.instagram, item.instagramUrl, item.instagram_url),
    firstFor("facebook", item.facebook, item.facebookUrl, item.facebook_url),
    first(item.youtube, item.youtubeUrl, item.youtube_url),
    firstFor("tiktok", item.tiktok, item.tiktokUrl, item.tiktok_url),
    first(item.website, item.websiteUrl, item.websiteLink, item.website_url),
  ].filter(Boolean).length;
}

export default function SocialLinks({
  item,
  px = 24,
  variant = "round",
}: {
  item: AnyItem;
  px?: number;
  /** "round" = circular icon-only badges. "bar" = full-width rectangular
   * button-bar style (matches the "View Location" button look). "sleek" =
   * compact full-width bar matching the small "Visit Website" button look. */
  variant?: "round" | "bar" | "sleek";
}) {
  const svg = px * 0.5;
  const links = [
    {
      key: "instagram",
      url: firstFor("instagram", item.instagram, item.instagramUrl, item.instagram_url),
      title: "Instagram",
      cls: "bg-gradient-to-br from-pink-500 to-orange-400",
      path: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z",
    },
    {
      key: "facebook",
      url: firstFor("facebook", item.facebook, item.facebookUrl, item.facebook_url),
      title: "Facebook",
      cls: "bg-[#1877F2]",
      path: "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z",
    },
    {
      key: "youtube",
      url: first(item.youtube, item.youtubeUrl, item.youtube_url),
      title: "YouTube",
      cls: "bg-[#FF0000]",
      path: "M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z",
    },
    {
      key: "tiktok",
      url: firstFor("tiktok", item.tiktok, item.tiktokUrl, item.tiktok_url),
      title: "TikTok",
      cls: "bg-black",
      path: "M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z",
    },
    {
      key: "website",
      url: first(item.website, item.websiteUrl, item.websiteLink, item.website_url),
      title: "Website",
      cls: "bg-[#0891b2]",
      path: "M12 2a10 10 0 100 20 10 10 0 000-20zm6.93 6h-2.95a15.65 15.65 0 00-1.38-3.56A8.03 8.03 0 0118.93 8zM12 4c.83 1.2 1.48 2.53 1.91 4h-3.82c.43-1.47 1.08-2.8 1.91-4zM4.26 14a7.96 7.96 0 010-4h3.38a16.6 16.6 0 000 4H4.26zm.81 2h2.95c.32 1.25.78 2.45 1.38 3.56A7.99 7.99 0 015.07 16zm2.95-8H5.07a7.99 7.99 0 014.33-3.56A15.65 15.65 0 008.02 8zM12 20c-.83-1.2-1.48-2.53-1.91-4h3.82A13.9 13.9 0 0112 20zm2.34-6H9.66a14.7 14.7 0 010-4h4.68a14.7 14.7 0 010 4zm.26 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95a7.99 7.99 0 01-4.33 3.56zM16.36 14a16.6 16.6 0 000-4h3.38a7.96 7.96 0 010 4h-3.38z",
    },
  ].filter((l) => l.url);

  if (!links.length) return null;

  if (variant === "sleek") {
    return (
      <div className="flex flex-col gap-1">
        {links.map((l) => (
          <a
            key={l.key}
            href={l.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className={`w-full flex items-center justify-center gap-0.5 text-white text-[9px] font-semibold py-1 rounded-sm transition-colors duration-200 cursor-pointer ${l.cls}`}
          >
            <svg style={{ width: 10, height: 10 }} fill="currentColor" viewBox="0 0 24 24">
              <path d={l.path} />
            </svg>
            {l.title}
          </a>
        ))}
      </div>
    );
  }

  if (variant === "bar") {
    return (
      <div className="flex flex-col gap-1.5">
        {links.map((l) => (
          <a
            key={l.key}
            href={l.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className={`inline-flex items-center justify-center gap-1.5 w-full font-semibold text-white rounded-lg transition-colors duration-200 px-3 py-1.5 text-[12px] ${l.cls}`}
          >
            <svg style={{ width: 14, height: 14 }} fill="currentColor" viewBox="0 0 24 24">
              <path d={l.path} />
            </svg>
            {l.title}
          </a>
        ))}
      </div>
    );
  }

  return (
    <div className="flex gap-1.5 flex-wrap">
      {links.map((l) => (
        <a
          key={l.key}
          href={l.url}
          target="_blank"
          rel="noopener noreferrer"
          title={l.title}
          onClick={(e) => e.stopPropagation()}
          className={`flex items-center justify-center rounded-full text-white hover:scale-110 transition-transform duration-200 ${l.cls}`}
          style={{ width: px, height: px }}
        >
          <svg style={{ width: svg, height: svg }} fill="currentColor" viewBox="0 0 24 24">
            <path d={l.path} />
          </svg>
        </a>
      ))}
    </div>
  );
}
